import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, Award } from "lucide-react";
import { getAllAchievementsWithStatus } from "../gamification";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";

const TIER_TONES = {
  bronze:  { tone: "rust", spine: "var(--rust)" },
  silver:  { tone: "neutral", spine: "var(--ink-300)" },
  gold:    { tone: "gold", spine: "var(--gold-500)" },
  diamond: { tone: "sky",  spine: "var(--sky)" },
};

const TIER_ORDER = { bronze: 0, silver: 1, gold: 2, diamond: 3 };

export default function Achievements() {
  const [filter, setFilter] = useState("all");
  const [refreshTick, setRefreshTick] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const achievements = useMemo(() => getAllAchievementsWithStatus(), [refreshTick]);

  useEffect(() => {
    const onFocus = () => setRefreshTick((t) => t + 1);
    const onUnlock = () => setRefreshTick((t) => t + 1);
    window.addEventListener("focus", onFocus);
    window.addEventListener("achievement:unlocked", onUnlock);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("achievement:unlocked", onUnlock);
    };
  }, []);

  const unlockedCount = achievements.filter((a) => a.unlocked).length;
  const totalCount    = achievements.length;
  const pct           = Math.round((unlockedCount / totalCount) * 100);

  const categories = [...new Set(achievements.map((a) => a.category))];
  const filtered = filter === "all" ? achievements : achievements.filter((a) => a.category === filter);

  const grouped = useMemo(() => {
    const map = {};
    filtered
      .sort((a, b) => (TIER_ORDER[a.tier] || 0) - (TIER_ORDER[b.tier] || 0))
      .forEach((a) => {
        if (!map[a.category]) map[a.category] = [];
        map[a.category].push(a);
      });
    return map;
  }, [filtered]);

  return (
    <div className="page-enter" style={{ paddingBottom: 48, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Link to="/dashboard" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 12 }}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Dashboard
        </Link>
        <Chip tone="gold">Achievements</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          Quiet <em style={{ color: "var(--gold-500)" }}>seals.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, maxWidth: "60ch", lineHeight: 1.6, margin: 0 }}>
          {unlockedCount} of {totalCount} earned. Each one is a small, specific win — no fanfare.
        </p>
      </div>

      {/* Progress band */}
      <Card paperDeep>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 12, gap: 16, flexWrap: "wrap" }}>
          <div>
            <span className="eyebrow">Your collection</span>
            <p className="font-display" style={{ fontSize: 56, lineHeight: 1, margin: "4px 0 0", fontWeight: 400 }}>
              {unlockedCount}<span style={{ fontSize: 22, color: "var(--fg-3)" }}> / {totalCount}</span>
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["bronze", "silver", "gold", "diamond"].map((tier) => {
              const total = achievements.filter((a) => a.tier === tier).length;
              const got   = achievements.filter((a) => a.tier === tier && a.unlocked).length;
              return (
                <div key={tier} style={{ background: "#fff", border: `1px solid ${TIER_TONES[tier].spine}40`, borderTop: `3px solid ${TIER_TONES[tier].spine}`, borderRadius: "var(--radius-sm)", padding: "8px 12px", textAlign: "center", minWidth: 60 }}>
                  <p className="font-display" style={{ fontSize: 22, lineHeight: 1, margin: 0, fontWeight: 400 }}>{got}<span style={{ fontSize: 12, color: "var(--fg-3)" }}>/{total}</span></p>
                  <p style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--fg-3)", margin: "4px 0 0" }}>{tier}</p>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ height: 4, background: "rgba(11,17,29,0.10)", borderRadius: 2, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: "var(--gold-500)", transition: "width 700ms cubic-bezier(.2,.8,.2,1)" }} />
        </div>
      </Card>

      {/* Filter chips */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <FilterChip label={`All (${totalCount})`} active={filter === "all"} onClick={() => setFilter("all")} />
        {categories.map((c) => {
          const total = achievements.filter((a) => a.category === c).length;
          const got   = achievements.filter((a) => a.category === c && a.unlocked).length;
          return (
            <FilterChip key={c} label={`${c} ${got}/${total}`} active={filter === c} onClick={() => setFilter(c)} />
          );
        })}
      </div>

      {/* Grouped grids */}
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          {filter === "all" && (
            <h2 className="eyebrow" style={{ marginBottom: 12 }}>{cat}</h2>
          )}
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            {items.map((a) => <BadgeCard key={a.id} ach={a} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        padding: "6px 14px",
        background: active ? "var(--ink-900)" : "#fff",
        color: active ? "var(--paper)" : "var(--fg-2)",
        border: `1px solid ${active ? "var(--ink-900)" : "var(--border-2)"}`,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
      }}>
      {label}
    </button>
  );
}

function BadgeCard({ ach }) {
  const tier = TIER_TONES[ach.tier] || TIER_TONES.bronze;
  return (
    <div style={{
      position: "relative",
      padding: 18,
      borderRadius: "var(--radius-md)",
      background: ach.unlocked ? "var(--paper-deep)" : "transparent",
      border: `1px solid ${ach.unlocked ? tier.spine + "60" : "var(--border-1)"}`,
      borderTop: ach.unlocked ? `3px solid ${tier.spine}` : "1px solid var(--border-1)",
      opacity: ach.unlocked ? 1 : 0.55,
      textAlign: "center",
    }}>
      {!ach.unlocked && (
        <Lock size={12} strokeWidth={1.5} color="var(--fg-3)" style={{ position: "absolute", top: 8, right: 8 }} />
      )}
      <div style={{ width: 48, height: 48, borderRadius: 999, background: ach.unlocked ? "var(--paper)" : "var(--ink-100)", color: ach.unlocked ? tier.spine : "var(--ink-400)", display: "inline-flex", alignItems: "center", justifyContent: "center", margin: "0 0 10px", border: ach.unlocked ? `1px solid ${tier.spine}` : "1px solid var(--border-1)" }}>
        <Award size={22} strokeWidth={1.5} />
      </div>
      <h3 className="font-display" style={{ fontSize: 18, fontWeight: 400, margin: "0 0 4px", lineHeight: 1.15 }}>{ach.title}</h3>
      <p style={{ fontSize: 12, color: "var(--fg-3)", margin: 0, lineHeight: 1.4 }}>{ach.desc}</p>
    </div>
  );
}
